import React, { useEffect, useMemo, useState } from "react";
import { adminApi, photoboothGalleryApi, formatApiError, getToken } from "@/lib/api";
import { Image as ImageIcon, Upload, Trash2, Loader2, Download, KeyRound, Copy, Ban, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const UPLOAD_CONCURRENCY = 4;   // upload 4 photos in parallel

function AuthedThumb({ src, alt }) {
  const [blobUrl, setBlobUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    (async () => {
      try {
        const token = getToken();
        const r = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const blob = await r.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch { /* ignore */ }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);
  if (!blobUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black/60">
        <ImageIcon size={24} strokeWidth={1} className="text-white/25" />
      </div>
    );
  }
  return <img src={blobUrl} alt={alt} className="w-full h-full object-cover" loading="lazy" />;
}

export default function AdminPhotoboothGallery() {
  const [clients, setClients] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventLabel, setEventLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadDone, setUploadDone] = useState(0);
  const [uploadFail, setUploadFail] = useState(0);
  const [filter, setFilter] = useState("");

  // Share codes
  const [shares, setShares] = useState([]);
  const [generatingCode, setGeneratingCode] = useState(false);

  useEffect(() => {
    adminApi.users().then((u) => setClients(u.filter((x) => x.role === "client"))).catch(() => {});
  }, []);

  const reload = () => {
    if (!selectedUserId) {
      setPhotos([]);
      setShares([]);
      return;
    }
    setLoading(true);
    photoboothGalleryApi.adminList(selectedUserId)
      .then(setPhotos)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    photoboothGalleryApi.adminListShares(selectedUserId)
      .then(setShares)
      .catch(() => setShares([]));
  };
  useEffect(reload, [selectedUserId]);

  const clientLabel = (u) => {
    const name = [u.prenom, u.nom].filter(Boolean).join(" ").trim() || u.name || "—";
    return `${name} · ${u.email}`;
  };

  const filteredClients = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return clients;
    return clients.filter((u) =>
      [u.email, u.prenom, u.nom, u.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(f))
    );
  }, [clients, filter]);

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selectedUserId) {
      toast.error("Sélectionnez d'abord un client.");
      return;
    }
    if (files.length === 0) return;

    setUploading(true);
    setUploadTotal(files.length);
    setUploadDone(0);
    setUploadFail(0);
    setPending(files.length);

    let idx = 0;
    let okCount = 0;
    let failCount = 0;
    // Worker pool: N concurrent uploads
    const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, async () => {
      while (idx < files.length) {
        const myIdx = idx++;
        const f = files[myIdx];
        try {
          await photoboothGalleryApi.adminUpload({ user_id: selectedUserId, event_label: eventLabel, file: f });
          okCount += 1;
          setUploadDone((n) => n + 1);
        } catch (err) {
          failCount += 1;
          setUploadFail((n) => n + 1);
          console.error("Upload failed:", f.name, err);
        } finally {
          setPending((n) => n - 1);
        }
      }
    });
    await Promise.all(workers);

    setUploading(false);
    setPending(0);
    if (okCount > 0) toast.success(`${okCount} photo${okCount > 1 ? "s" : ""} envoyée${okCount > 1 ? "s" : ""}.`);
    if (failCount > 0) toast.error(`${failCount} échec${failCount > 1 ? "s" : ""} d'envoi.`);
    reload();
  };

  const generateAndSendCode = async () => {
    if (!selectedUserId) return;
    if (!window.confirm("Générer un code d'accès et l'envoyer par email au client ?")) return;
    setGeneratingCode(true);
    try {
      const r = await photoboothGalleryApi.adminGenerateCode({
        user_id: selectedUserId,
        event_label: eventLabel,
        send_email: true,
        validity_days: 90,   // 3 mois
      });
      toast.success(`Code ${r.code} généré et envoyé au client.`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyCode = (code) => {
    const url = `${window.location.origin}/galerie/${code}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Lien copié dans le presse-papiers."),
      () => toast.error("Impossible de copier."),
    );
  };

  const revokeCode = async (code) => {
    if (!window.confirm(`Révoquer le code ${code} ? Le client ne pourra plus y accéder.`)) return;
    try {
      await photoboothGalleryApi.adminRevokeShare(code);
      toast.success("Code révoqué.");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const delPhoto = async (id) => {
    if (!window.confirm("Supprimer cette photo de la galerie du client ?")) return;
    try {
      await photoboothGalleryApi.adminDelete(id);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-photobooth-gallery">
      <header className="mb-8">
        <p className="eyebrow">Galeries photobooth</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-2">Gestion des <span className="italic gold-text">galeries.</span></h1>
        <p className="text-white/55 text-sm mt-3 max-w-2xl">
          Sélectionnez un client, indiquez un libellé d'événement (facultatif) puis téléchargez les photos de son événement.
          Le client verra automatiquement ses photos dans son espace « Mon Photobooth ».
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4">
          <div className="card-luxe p-5">
            <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">Rechercher un client</label>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="pbg-filter"
              placeholder="Nom, prénom ou email…"
              className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none"
            />
            <div className="mt-4 max-h-[420px] overflow-y-auto border border-[#DCB457]/15 divide-y divide-[#DCB457]/10" data-testid="pbg-clients-list">
              {filteredClients.length === 0 && <p className="p-4 text-sm text-white/45 italic">Aucun client.</p>}
              {filteredClients.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelectedUserId(c.id)}
                  data-testid={`pbg-select-${c.id}`}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-[#DCB457]/5 transition ${selectedUserId === c.id ? "bg-[#DCB457]/10 border-l-2 border-l-[#DCB457]" : ""}`}
                >
                  <p className="font-serif-luxe">{[c.prenom, c.nom].filter(Boolean).join(" ").trim() || c.name || "—"}</p>
                  <p className="text-[11px] text-white/50 truncate">{c.email}</p>
                </button>
              ))}
            </div>
          </div>
        </aside>

        <section className="lg:col-span-8 min-w-0">
          {!selectedUserId ? (
            <div className="card-luxe p-10 text-center">
              <ImageIcon size={30} strokeWidth={1} className="text-[#DCB457] mx-auto mb-3" />
              <p className="text-white/60 text-sm">Sélectionnez un client à gauche pour gérer sa galerie photobooth.</p>
            </div>
          ) : (
            <>
              <div className="card-luxe p-6 mb-6">
                <p className="eyebrow mb-2">Client sélectionné</p>
                <p className="font-serif-luxe text-xl">
                  {clientLabel(clients.find((x) => x.id === selectedUserId) || {})}
                </p>

                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                  <div className="md:col-span-2">
                    <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">Libellé de l'événement (facultatif)</label>
                    <input
                      value={eventLabel}
                      onChange={(e) => setEventLabel(e.target.value)}
                      data-testid="pbg-event-label"
                      placeholder="ex. Mariage 12 juillet 2025 — Salle Odyssée"
                      className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm focus:border-[#DCB457] outline-none"
                    />
                  </div>
                  <label
                    data-testid="pbg-upload-btn"
                    className={`btn-gold inline-flex items-center justify-center gap-2 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                  >
                    {uploading ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Envoi… {pending > 0 ? `(${pending} restant)` : ""}
                      </>
                    ) : (
                      <>
                        <Upload size={14} strokeWidth={1.5} /> Téléverser des photos
                      </>
                    )}
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={onFilesSelected}
                      className="hidden"
                      data-testid="pbg-upload-input"
                    />
                  </label>
                </div>
                <p className="text-[11px] text-white/40 mt-3">Formats acceptés : JPG, PNG, WEBP, HEIC · 30 Mo max par photo · Envois en parallèle (×{UPLOAD_CONCURRENCY}).</p>

                {uploading && (
                  <div className="mt-4" data-testid="pbg-upload-progress">
                    <div className="flex items-center justify-between text-[11px] text-white/60 mb-1.5">
                      <span>Envoi de {uploadDone + uploadFail} / {uploadTotal} photo(s)…</span>
                      {uploadFail > 0 && <span className="text-red-300">{uploadFail} échec(s)</span>}
                    </div>
                    <div className="h-1.5 bg-white/10 overflow-hidden">
                      <div
                        className="h-full bg-[#DCB457] transition-all"
                        style={{ width: `${Math.round(((uploadDone + uploadFail) / Math.max(1, uploadTotal)) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Code d'accès partagé */}
              <div className="card-luxe p-6 mb-6" data-testid="pbg-shares-card">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="eyebrow mb-1">Accès par code sécurisé</p>
                    <p className="text-xs text-white/55">Envoyez au client un code unique pour consulter et télécharger ses photos sans compte.</p>
                  </div>
                  <button
                    type="button"
                    onClick={generateAndSendCode}
                    disabled={generatingCode || photos.length === 0}
                    data-testid="pbg-generate-code-btn"
                    className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
                    title={photos.length === 0 ? "Téléversez au moins une photo avant de générer un code" : ""}
                  >
                    {generatingCode ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} strokeWidth={1.5} />}
                    Générer & envoyer un code
                  </button>
                </div>
                {shares.length > 0 ? (
                  <div className="mt-4 space-y-2" data-testid="pbg-shares-list">
                    {shares.map((s) => {
                      const revoked = s.is_revoked;
                      const expired = s.expires_at && new Date(s.expires_at) < new Date();
                      const inactive = revoked || expired;
                      return (
                        <div
                          key={s.code}
                          className={`flex items-center gap-3 border border-[#DCB457]/15 px-3 py-2 text-sm ${inactive ? "opacity-50" : ""}`}
                          data-testid={`pbg-share-${s.code}`}
                        >
                          <code className="font-mono tracking-widest text-[#DCB457] text-base">{s.code}</code>
                          <span className="text-white/55 text-xs flex-1 truncate">
                            {s.event_label || "—"} · {s.views || 0} vue(s) · {s.downloads || 0} téléchargement(s)
                            {revoked && " · révoqué"}
                            {!revoked && expired && " · expiré"}
                          </span>
                          {!inactive && (
                            <>
                              <button
                                type="button"
                                onClick={() => copyCode(s.code)}
                                title="Copier le lien"
                                data-testid={`pbg-copy-${s.code}`}
                                className="text-white/60 hover:text-[#DCB457]"
                              >
                                <Copy size={13} strokeWidth={1.5} />
                              </button>
                              <a
                                href={`/galerie/${s.code}`}
                                target="_blank"
                                rel="noreferrer"
                                title="Ouvrir dans un nouvel onglet"
                                className="text-white/60 hover:text-[#DCB457]"
                              >
                                <ExternalLink size={13} strokeWidth={1.5} />
                              </a>
                              <button
                                type="button"
                                onClick={() => revokeCode(s.code)}
                                title="Révoquer"
                                data-testid={`pbg-revoke-${s.code}`}
                                className="text-red-300 hover:text-red-400"
                              >
                                <Ban size={13} strokeWidth={1.5} />
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-white/50 text-xs italic mt-3">Aucun code généré pour ce client.</p>
                )}
              </div>

              <div className="card-luxe p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="font-serif-luxe text-xl">Photos du client ({photos.length})</h2>
                </div>
                {loading ? (
                  <p className="text-white/55 text-sm italic flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Chargement…</p>
                ) : photos.length === 0 ? (
                  <p className="text-white/55 text-sm italic">Aucune photo dans la galerie de ce client.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="pbg-photos-grid">
                    {photos.map((p) => (
                      <div key={p.id} className="relative border border-[#DCB457]/15 aspect-square overflow-hidden group" data-testid={`pbg-photo-${p.id}`}>
                        <AuthedThumb src={photoboothGalleryApi.adminFileUrl(p.id)} alt={p.original_filename} />
                        <div className="absolute inset-x-0 bottom-0 p-2 flex items-center justify-between bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition">
                          <span className="text-[10px] text-white/70 truncate mr-2">{p.event_label || p.original_filename}</span>
                          <button
                            type="button"
                            onClick={() => delPhoto(p.id)}
                            data-testid={`pbg-del-${p.id}`}
                            className="text-red-300 hover:text-red-400"
                            title="Supprimer"
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
